/**
 * LIVE brand typography (2026-08 identity refresh). `theme/fonts.ts` re-exports
 * `brandFonts` as the theme's `fonts`, so this file IS the shipping type stack
 * — not a proposal. The retired families were deleted along with the dev-only
 * A/B preview page once the brand type was signed off.
 *
 * Role mapping vs. the pre-2026-08 stack:
 *
 *   role      was                    now
 *   ────────  ─────────────────────  ─────────────────────────
 *   body      Inter                  Suisse Int'l
 *   heading   Geist Sans             GT Ultra Median
 *   display   Playfair Display       GT Ultra Median
 *   wordmark  DM Serif Display       GT Ultra Median
 *   mono      Rubik                  GT Pressura Mono (2026-09)
 *
 * Collapsing display + wordmark into the heading face is deliberate: GT Ultra
 * is a sans/serif hybrid that already carries the editorial voice Playfair and
 * DM Serif were brought in for, so keeping them would mean three display
 * families doing one job. Net families drop 5 → 3, and two render-blocking
 * @fontsource families leave the bundle.
 *
 * `mono` became GT Pressura Mono in the 2026-09 brand pass, and that is an
 * upgrade on both of this role's jobs rather than a trade.
 *
 * MEASURED, in a browser, off live DOM elements at 100px (2026-09-12):
 *
 *                     M       W       i       l       0       1       9
 *   GT Pressura Mono  55.00   55.00   55.00   55.00   55.00   55.00   55.00
 *   Rubik             79.41   80.10   24.20   24.30   63.50   42.80   59.30
 *
 * Pressura is a TRUE monospace — every glyph one advance width. Rubik is a
 * proportional sans that was never monospaced at all, and its figures are not
 * even uniform, so `textStyle="numeric"` only ever tabulated because of the
 * `"tnum"` feature setting `theme/index.ts` applies on top.
 *
 * That matters to three consumers this role has always had, all of which were
 * quietly wrong before and are now right: `CodeBlock` (`as="pre"`),
 * `ErrorBoundary`'s stack traces, and `AuditTimelineEvent`, which sizes a
 * column with `minW="14ch"` — `ch` is the advance of "0", which only equals a
 * character cell when the face is genuinely monospaced.
 *
 * Rubik is GONE from this stack, not demoted to fallback — see the comment on
 * MONO below for why a proportional fallback is worse than a system one.
 *
 * MEASURE, DO NOT ASSUME — in either direction. A first pass at this measured
 * through a canvas 2d context before the webfont had loaded, read the
 * fallback's proportional metrics, and concluded the opposite. Take the
 * numbers off a rendered element and check `getComputedStyle().fontFamily`
 * actually resolved to the face you think you are measuring.
 */

const SANS_BODY =
  '"Suisse Intl", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';
const DISPLAY =
  '"GT Ultra Median", "Suisse Intl", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
// NO Rubik in this stack. It was the previous primary and the obvious thing to
// leave behind as a fallback, but it is PROPORTIONAL with non-uniform figures
// (see the table above), so at fallback position one it would hand a failed
// webfont load the exact defect the swap removed — misaligned code and numeric
// columns — while looking like a safety net. `ui-monospace` is monospaced and
// tabular by construction on every platform, so it is the better next face.
const MONO =
  '"GT Pressura Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export const brandFonts = {
  heading: DISPLAY,
  body: SANS_BODY,
  mono: MONO,
  display: DISPLAY,
  serif: DISPLAY,
  wordmark: DISPLAY,
};
